import type { WorkItemStatus, WorkItemSummary } from '../types/workflow.js';

/** Mirrors the server presence selector, including malformed and gate-only carriers. */
export function isNoSecondTry(item: WorkItemSummary): boolean {
  return Object.hasOwn(item, 'noSecondTryGate') || [item.suggestedPatch, item.reviewerPatch]
    .some(patch => patch !== null && typeof patch === 'object' && Object.hasOwn(patch, '_no_second_try'));
}

export function effectiveWorkItemPatch(item: WorkItemSummary): Record<string, unknown> {
  return item.reviewerPatch ?? item.suggestedPatch;
}

export function isStepAppend(item: WorkItemSummary): boolean {
  return Object.hasOwn(item.suggestedPatch, '_append') || Object.hasOwn(item.reviewerPatch ?? {}, '_append');
}

/**
 * A new lesson or a fill at a reviewed position in a reading. It is approved on its lesson-review page, where the
 * placement and prerequisites are checked against the live course — never with one click from the queue (Opus review of
 * the web fix, P2-W1: an approval the release is certain to refuse is not shippable).
 */
export function isPlacedLesson(item: WorkItemSummary): boolean {
  return isLessonFill(item) || (item.op === 'create' && item.targetType === 'entity' && !!item.lessonPlacement);
}

export function isLessonFill(item: WorkItemSummary): boolean {
  return item.op === 'rewrite' && item.targetType === 'entity' && !!item.lessonFill;
}

export function isLessonDraft(item: WorkItemSummary): boolean {
  if (isLessonFill(item)) return true;
  if (item.op !== 'create' || item.targetType !== 'entity') {
    return false;
  }
  const steps = effectiveWorkItemPatch(item).steps;
  return Array.isArray(steps) && steps.length > 0;
}

export function isPracticeItem(item: WorkItemSummary): boolean {
  return item.taskType === 'practice_item' || isNoSecondTry(item) && item.targetType === 'task';
}

export type PracticeItemAttachment = 'unattached' | 'attached' | null;

/**
 * Attachment state for decoupled practice items (ELPI lane), driven ONLY by the persisted
 * work-item fields — `intendedParent` marks the lane, the reconciler-mirrored
 * `attachedEntityId` marks binding. Never inferred from patch contents (red-team #9).
 */
export function practiceItemAttachment(item: WorkItemSummary): PracticeItemAttachment {
  if (!item.intendedParent) {
    return null;
  }
  return item.attachedEntityId != null ? 'attached' : 'unattached';
}

export function statusBadgeClass(status: WorkItemStatus): string {
  switch (status) {
    case 'approved':
    case 'approved_with_edit':
      return 'rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-bold uppercase text-emerald-600';
    case 'executed':
      return 'rounded-full bg-slate-100 px-2 py-1 text-[10px] font-bold uppercase text-slate-600';
    case 'cancelled':
      return 'rounded-full bg-red-50 px-2 py-1 text-[10px] font-bold uppercase text-red-500';
    default:
      return 'rounded-full bg-blue-50 px-2 py-1 text-[10px] font-bold uppercase text-blue-600';
  }
}

/**
 * The transport seam for a rejection reason.
 *
 * The reason is currently folded into the free-text `notes` string that `cancelItem(id, notes?)`
 * already carries end to end (review event + audit row). Storing it as a real enum instead is a
 * pending owner decision; when it lands, **this function is where the reason value changes shape**
 * — no review surface formats it anywhere else. Note the swap is not free elsewhere: `cancelItem`
 * in `api/client.ts` decides the request body, and the dashboard's `onCancel(notes?: string)` prop
 * type would follow (verified with gpt-5.6-sol, 2026-07-30).
 */
export function buildCancelNotes(reason: string, note: string): string | undefined {
  const trimmedNote = note.trim();
  if (reason && trimmedNote) {
    return `${reason}: ${trimmedNote}`;
  }
  return reason || trimmedNote || undefined;
}

export function queueContextParams(params: URLSearchParams): URLSearchParams {
  const context = new URLSearchParams();
  for (const key of ['courseKey', 'sectionId', 'status']) {
    const value = params.get(key);
    if (value) {
      context.set(key, value);
    }
  }
  return context;
}

export function lessonReviewPath(workItemId: string, params: URLSearchParams): string {
  const context = queueContextParams(params).toString();
  const path = `/queue/lesson/${encodeURIComponent(workItemId)}`;
  return context ? `${path}?${context}` : path;
}

/**
 * `targetId` is a string on a work item but a number in the stored lesson/task document. Keep this
 * conversion aligned with the executor, which applies `Number(targetId)`: rejecting a value here
 * that the executor accepts would hide the read-only view for a target the workflow can act on.
 */
export function lessonTranscriptPath(item: WorkItemSummary, params: URLSearchParams): string | null {
  if (item.targetType !== 'entity' && item.targetType !== 'task') {
    return null;
  }
  if (!item.targetId || !item.courseKey) {
    return null;
  }
  const id = Number(item.targetId);
  if (!Number.isFinite(id)) {
    return null;
  }

  const targetCollection = item.targetType === 'entity' ? 'entities' : 'tasks';
  const context = queueContextParams(params).toString();
  const path = `/courses/${encodeURIComponent(item.courseKey)}/${targetCollection}/${id}/transcript`;
  return context ? `${path}?${context}` : path;
}

export function practiceReviewPath(workItemId: string, params: URLSearchParams): string {
  const context = queueContextParams(params).toString();
  const path = `/queue/practice/${encodeURIComponent(workItemId)}`;
  return context ? `${path}?${context}` : path;
}

export function queueBackPath(params: URLSearchParams): string {
  const context = queueContextParams(params).toString();
  return context ? `/queue?${context}` : '/queue';
}
